import { forwardRef, useEffect, useRef, useState } from 'react'
import type React from 'react'
import {
  type ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'

import { cn } from '@modules/lib/lib'

import { isPointOverRenderedImage } from '../utils/isPointOverRenderedImage'

interface ZoomableImageProps {
  src: string
  alt: string
  /** ピンドラッグ中かどうか。trueの場合、ズーム/パン機能を一時的に無効化する */
  isDragging?: boolean
  /**
   * ドラッグによるパンのみを無効化する（選択モード中に使用）。
   * ホイール/ピンチによるズームは維持される。
   */
  panDisabled?: boolean
  /** キャンバス移動モード中の grab カーソル表示（Figma の hand ツール同様の視覚フィードバック） */
  grabCursor?: boolean
  /**
   * 最小ズーム倍率。既定 1（= コンテナに内接した初期表示より縮小できない）。
   * 1 未満を渡すと初期表示よりさらに縮小でき、縦長画像を画面に収められる。
   */
  minScale?: number
  /**
   * 最大ズーム倍率。既定 4（= 400%）。積算の図面キャンバスは細かい開口部に点を合わせるため
   * 8（800%）を渡す（PdM FB 2026-09-25 #4）。AIカラーは既定のまま。
   */
  maxScale?: number
  /**
   * ズーム倍率の変化通知。頂点マーカー等をズームに依らずスクリーン上一定サイズに
   * 保つための補正 (1/scale) に使う。パン中は scale が変わらないため再レンダーは発生しない
   */
  onScaleChange?: (scale: number) => void
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void
  children?: React.ReactNode
}

/**
 * ズーム・パン機能を持つ画像コンポーネント
 * ref経由でresetTransform等のズーム制御メソッドにアクセス可能
 *
 * ズーム操作の規則（issue #2439。AIカラー・積算の両キャンバスに適用）:
 * - 修飾キーなしのホイールは、ポインタが**画像の描画範囲の上**にあるときだけズームする。
 *   範囲外（縮小時に画像の周りにできる余白）ではズームせず、ページのスクロールに任せる
 * - ピンチ / Ctrl+ホイール（ctrlKey 付き wheel）は意図的なズームなので、枠内ならどこでもズームする
 * - ダブルクリックでは拡大しない
 */
export const ZoomableImage = forwardRef<ReactZoomPanPinchRef, ZoomableImageProps>(
  (
    {
      src,
      alt,
      isDragging = false,
      panDisabled = false,
      grabCursor = false,
      minScale = 1,
      maxScale = 4,
      onScaleChange,
      onLoad,
      children,
    },
    ref,
  ) => {
    const imgRef = useRef<HTMLImageElement>(null)
    // ライブラリがホイールのリスナーを付けるラッパー要素。onInit で受け取る
    const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null)

    useEffect(() => {
      if (!wrapperElement) return
      // react-zoom-pan-pinch はラッパー要素に（bubble の）wheel リスナーを付け、ポインタが画像の上かを見ずに
      // ズームする。同じ要素の capture フェーズで先回りし、画像の外なら伝播を止めてライブラリに届かせない。
      // preventDefault はしないので、ブラウザ既定のページスクロールはそのまま行われる
      const handleWheelCapture = (event: WheelEvent) => {
        // ピンチ / Ctrl+ホイールは素通し。止めるとキャンバスがズームしない代わりに
        // ブラウザのページ全体のズームが発火してしまう
        if (event.ctrlKey) return
        const img = imgRef.current
        if (!img) return
        const isOverImage = isPointOverRenderedImage(
          img.getBoundingClientRect(),
          img.naturalWidth,
          img.naturalHeight,
          event.clientX,
          event.clientY,
        )
        if (!isOverImage) {
          // ターゲットがラッパー自身（余白部分）の場合は同一要素上の bubble リスナーとの順序になるため、
          // stopPropagation ではなく stopImmediatePropagation で確実に止める
          event.stopImmediatePropagation()
        }
      }
      wrapperElement.addEventListener('wheel', handleWheelCapture, { capture: true, passive: true })
      return () => {
        wrapperElement.removeEventListener('wheel', handleWheelCapture, { capture: true })
      }
    }, [wrapperElement])

    return (
      <TransformWrapper
        ref={ref}
        initialScale={1}
        minScale={minScale}
        maxScale={maxScale}
        wheel={{ step: 0.1, disabled: isDragging }}
        panning={{ disabled: isDragging || panDisabled }}
        pinch={{ disabled: isDragging }}
        // ダブルクリック拡大は廃止（issue #2439）。ライブラリはラッパー要素にネイティブの dblclick を
        // 付けるため、頂点マーカーの React onDoubleClick（ルート委譲）で伝播を止めても先に発火し、
        // 頂点削除と同時に画像が拡大されていた
        doubleClick={{ disabled: true }}
        disabled={isDragging}
        // 再マウント時 (タブ切替の key 変更等) は onTransformed が発火しないため、
        // onInit で初期倍率を通知して親側の scale state が前タブの値のまま残るのを防ぐ
        onInit={instance => {
          setWrapperElement(instance.instance.wrapperComponent)
          onScaleChange?.(instance.state.scale)
        }}
        onTransformed={(_, state) => onScaleChange?.(state.scale)}
      >
        <TransformComponent
          wrapperClass={cn('!w-full !h-full', grabCursor && 'cursor-grab')}
          contentClass="!w-full !h-full"
        >
          <img
            alt={alt}
            className="object-contain w-full h-full"
            onLoad={onLoad}
            ref={imgRef}
            src={src}
          />
          {children}
        </TransformComponent>
      </TransformWrapper>
    )
  },
)

ZoomableImage.displayName = 'ZoomableImage'
